import React, { useMemo, useCallback } from "react";
import Select from "@semcore/ui/select";
import Input from "@semcore/ui/input";
import { Box } from "@semcore/ui/flex-box";
import { useSelector } from "react-redux";
import HistoryList from "../../pages/Search-History/getHistoryList";
import { ZustandStore } from "../../../store/zustand-store";
import { useNavigate } from "react-router-dom";
import SemButton from "../../../common/components/Button";
import Divider from "@semcore/ui/divider";
import { t } from "i18next";

const Highlight = ({ highlight, children }) => {
  let html = children;
  if (highlight) {
    try {
      const re = new RegExp(highlight.toLowerCase(), "g");
      html = html.replace(
        re,
        `<span style="font-weight: bold; padding: 2px 0">${highlight}</span>`
      );
    } catch (e) {}
  }
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
};

const DashSearchbar = () => {
  const navigate = useNavigate();
  HistoryList();

  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [visible, setVisible] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const { history_data } = ZustandStore((state) => state);
  const userallprojectname = useSelector((state) => state.userallprojectname);

  const projectList = useMemo(() => {
    if (!userallprojectname || userallprojectname.length === 0) return [];
    return [
      ...new Set(userallprojectname.map((project) => project.split(",")[0])),
    ];
  }, [userallprojectname]);

  const lastSearchData = useMemo(() => {
    function getLastSearchData(type) {
      const data = history_data.data
        .filter((item) => item.type === type)
        .map((item) => {
          if (type === "site") {
            const match = item.data.match(
              /^(?:https?:\/\/)?(?:www\.)?([^/]+)/i
            );
            const domain = match ? match[1].replace(/\s/g, "") : "";
            return { data: domain };
          } else {
            return item;
          }
        });
      const uniqueArray = data?.reduce((acc, item) => {
        if (!acc.some((t) => t.data === item.data)) {
          acc.push(item);
        }
        return acc;
      }, []);
      return uniqueArray.slice(-3).reverse();
    }

    if (!history_data?.data || history_data?.data?.length === 0) return [];
    return {
      keywords: getLastSearchData("keyword", history_data.data),
      domains: getLastSearchData("site", history_data.data),
    };
  }, [history_data]);

  const handleSiteOverviewUpdate = (domain) => {
    sessionStorage.removeItem("domainCountry");
    sessionStorage.setItem("lastdomainname", domain);
    ZustandStore.setState({
      worldwide_domain: null,
      site_overview_worldwide: {
        domain: "",
        data: [],
      },
      site_overview_backlink_data: [],
      site_overview_page_data: [],
    });
    setQuery("");
    navigate("/site-overview");
  };

  const handleSearchInput = useCallback((keyword) => {
    if (!keyword) return;
    const urlPattern =
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
    const domainPattern = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    if (domainPattern.test(keyword)) {
      handleSiteOverviewUpdate(keyword);
    } else if (urlPattern.test(keyword)) {
      const match = keyword.match(/^(?:https?:\/\/)?(?:www\.)?([^/]+)/i);
      const domain = match ? match[1].replace(/\s/g, "") : "";
      handleSiteOverviewUpdate(domain);
    } else {
      sessionStorage.removeItem("KEYWORD_DOMAIN_COUNTRY");
      sessionStorage.setItem("KEYWORD_DOMAIN_SEARCH", true);
      sessionStorage.setItem("KEYWORD_DOMAIN_NAME", keyword);
      ZustandStore.setState({
        keyword_research_data: {
          data: [],
          loading: false,
          search: false,
          error: false,
        },
        keyword_ques_based_data: {
          data: [],
          loading: false,
          search: false,
        },
        keyword_research_ideas_data: {
          data: [],
          loading: false,
          search: false,
        },
      });
      setQuery("");
      navigate("/keyword-research/overview");
    }
  }, []);

  const handleSelect = React.useCallback((x) => {
    setQuery(x);
    handleSearchInput(x);
    setVisible(false);
  }, []);

  const handleKeyDown = (e) => {
    if (!e.key.startsWith("Array")) {
      setHighlightedIndex(-1);
    }
    if (e.key === "Enter" && query) {
      handleSearchInput(query);
    }
  };

  const handleHighlightedIndexChange = (index) => {
    setHighlightedIndex(index);
  };

  const handleChangeVisible = (visible) => {
    setVisible(visible);
  };

  return (
    <Box className="navBar px-3 py-1 d-flex align-items-center">
      <Select
        interaction="focus"
        onChange={handleSelect}
        value={query}
        visible={visible}
        onVisibleChange={handleChangeVisible}
        highlightedIndex={highlightedIndex}
        onHighlightedIndexChange={handleHighlightedIndexChange}
        defaultHighlightedIndex={null}
      >
        <Select.Trigger tag={Input} wMax={"400px"} h={"35px"} mr={2}>
          {() => (
            <Input.Value
              value={query}
              role="combobox"
              placeholder={t("DeshSearchbar.placeholder")}
              onChange={setQuery}
              id="website-autosuggest"
              onKeyDown={handleKeyDown}
              autoComplete={"off"}
            />
          )}
        </Select.Trigger>
        <Select.Menu hMax={465}>
          {lastSearchData?.domains?.length > 0 ? (
            <>
              <Select.Group subTitle="Domains">
                {lastSearchData?.domains?.map(({ data }) => {
                  return (
                    <Select.Option value={data} key={data}>
                      <Highlight highlight={query}>{data}</Highlight>
                    </Select.Option>
                  );
                })}
              </Select.Group>
              <Divider h={1} />
            </>
          ) : null}
          {lastSearchData?.keywords?.length > 0 ? (
            <>
              <Select.Group subTitle="Keywords">
                {lastSearchData?.keywords?.map(({ data }) => {
                  return (
                    <Select.Option value={data} key={data}>
                      <Highlight highlight={query}>{data}</Highlight>
                    </Select.Option>
                  );
                })}
              </Select.Group>
              <Divider h={1} />
            </>
          ) : null}
          {projectList?.length > 0 ? (
            <Select.Group subTitle="Projects">
              {projectList?.map((project) => {
                return (
                  <Select.Option value={project} key={project} my={0}>
                    <Highlight highlight={query}>{project}</Highlight>
                  </Select.Option>
                );
              })}
            </Select.Group>
          ) : null}
        </Select.Menu>
      </Select>
      <SemButton
        disabled={!query}
        name={t("common_buttons.search")}
        size="m"
        w="80px"
        h="33px"
        onClick={() => handleSearchInput(query)}
      />
    </Box>
  );
};

export default DashSearchbar;
